/**
 * Fay-Herriot core: model fit (brief 9.6), between-area variance
 * estimation (9.7), EBLUP and synthetic estimates (9.8), and the
 * analytical MSE decomposition (9.9), plus the per-cell diagnostics of
 * 9.16 that the fit's inputs can support.
 *
 * The fitting formulas are the brief's O(m p^2) forms (no m-by-m
 * matrices): with q_i = 1 / (sigma2_v + psi_i),
 *   M = sum q_i x_i x_i',  M2 = sum q_i^2 x_i x_i',  M3 = sum q_i^3 x_i x_i'
 *   betahat = inv(M) sum q_i x_i thetahat_i,  r_i = thetahat_i - x_i' betahat
 *   trP  = sum q_i - tr(inv(M) M2)
 *   trP2 = sum q_i^2 - 2 tr(inv(M) M3) + tr((inv(M) M2)^2)
 *   REML score = -trP / 2 + sum q_i^2 r_i^2 / 2,  info = trP2 / 2.
 *
 * Fallback order is the brief's and is not configurable (9.7): REML by
 * Fisher scoring from max(sigma2_PR, 0.1 * median(psi)); the Li-Lahiri
 * adjusted REML when REML reaches the boundary at zero; Prasad-Rao when
 * REML does not converge (or the adjusted estimate when sigma2_PR is 0).
 *
 * Owner decision S4 sets the area-count policy: a fit with fewer than
 * five in-sample areas is refused (InsufficientAreasError carrying the
 * MET-14 issue). The companion warning below ten areas is surfaced by
 * the analysis layer on CellResult.issues — FayHerriotFit has no issue
 * channel of its own, and FayHerriotFitConfig carries no minimum-areas
 * setting (ModelConfig.minAreas lives in the analysis configuration).
 */
import { InsufficientAreasError } from './errors.js';
import { fitFayHerriotTImpl } from './fayherriot-t.js';
import { cholesky, cholInverse, dot, matMul, matVec, traceOfProduct, zeros, } from './linalg.js';
/** Decision S4: a fit is refused below this many in-sample areas. */
export const MIN_AREAS_TO_FIT = 5;
function meanOf(values) {
    return values.reduce((acc, v) => acc + v, 0) / values.length;
}
function medianOf(values) {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1
        ? sorted[mid]
        : 0.5 * (sorted[mid - 1] + sorted[mid]);
}
/** Sample variance with denominator n - 1 (0 when n < 2). */
function sampleVariance(values) {
    if (values.length < 2)
        return 0;
    const mean = meanOf(values);
    return (values.reduce((acc, v) => acc + (v - mean) * (v - mean), 0) /
        (values.length - 1));
}
function singularError(context) {
    return new Error(`fitFayHerriot: the ${context} matrix is singular or not positive definite; ` +
        'the covariates are collinear. Remove a collinear covariate and refit ' +
        '(brief 9.7 covariate dropping is not applied automatically: the fit ' +
        'result has no channel to report a dropped covariate).');
}
function weightedQuantities(y, X, psi, sigma2) {
    const m = y.length;
    const p = X[0].length;
    const M = zeros(p, p);
    const M2 = zeros(p, p);
    const M3 = zeros(p, p);
    const rhs = new Array(p).fill(0);
    let sumQ = 0;
    let sumQ2 = 0;
    for (let i = 0; i < m; i++) {
        const q = 1 / (sigma2 + psi[i]);
        const q2 = q * q;
        const q3 = q2 * q;
        sumQ += q;
        sumQ2 += q2;
        const xi = X[i];
        for (let a = 0; a < p; a++) {
            rhs[a] += q * xi[a] * y[i];
            for (let b = 0; b < p; b++) {
                const xab = xi[a] * xi[b];
                M[a][b] += q * xab;
                M2[a][b] += q2 * xab;
                M3[a][b] += q3 * xab;
            }
        }
    }
    const L = cholesky(M);
    if (L === null)
        throw singularError('weighted design (M)');
    const invM = cholInverse(L);
    const beta = matVec(invM, rhs);
    const residuals = y.map((yi, i) => yi - dot(X[i], beta));
    const K = matMul(invM, M2);
    let trK = 0;
    for (let a = 0; a < p; a++)
        trK += K[a][a];
    const trP = sumQ - trK;
    const trP2 = sumQ2 - 2 * traceOfProduct(invM, M3) + traceOfProduct(K, K);
    let sumQ2r2 = 0;
    for (let i = 0; i < m; i++) {
        const q = 1 / (sigma2 + psi[i]);
        sumQ2r2 += q * q * residuals[i] * residuals[i];
    }
    return {
        beta,
        invM,
        residuals,
        score: -0.5 * trP + 0.5 * sumQ2r2,
        info: 0.5 * trP2,
    };
}
/** Prasad-Rao moment estimator of sigma2_v (brief 9.7). */
function prasadRaoSigma2(y, X, psi) {
    const m = y.length;
    const p = X[0].length;
    const XtX = zeros(p, p);
    const Xty = new Array(p).fill(0);
    for (let i = 0; i < m; i++) {
        for (let a = 0; a < p; a++) {
            Xty[a] += X[i][a] * y[i];
            for (let b = 0; b < p; b++)
                XtX[a][b] += X[i][a] * X[i][b];
        }
    }
    const L = cholesky(XtX);
    if (L === null)
        throw singularError('cross-product (X\'X)');
    const invXtX = cholInverse(L);
    const betaOls = matVec(invXtX, Xty);
    let sumU2 = 0;
    let sumPsiH = 0;
    for (let i = 0; i < m; i++) {
        const u = y[i] - dot(X[i], betaOls);
        const h = dot(X[i], matVec(invXtX, X[i]));
        sumU2 += u * u;
        sumPsiH += psi[i] * (1 - h);
    }
    return Math.max(0, (sumU2 - sumPsiH) / (m - p));
}
/**
 * Li-Lahiri adjusted REML (brief 9.7): maximize l_R(sigma2) + ln(sigma2)
 * with Fisher-style steps on the adjusted score and information,
 * safeguarded onto (0, U] by step halving.
 */
/**
 * Convergence threshold for the sigma2_v iterations (directive C-8):
 * stop when the absolute step falls below tol * (sigma2_v + median
 * psi) — relative to the scale of the quantity being estimated and
 * of the sampling variances it is estimated against, rather than to
 * (1 + sigma2_v). On the arcsine scale (sigma2_v and psi both of
 * order 1e-2) this is far tighter than the old rule; on large-variance
 * linear-scale fits it scales with the problem instead of sitting at
 * an absolute 1e-8.
 */
function stepTolerance(tol, sigma2, medianPsi) {
    return tol * (sigma2 + medianPsi);
}
function fitAdjustedReml(y, X, psi, config, start) {
    const tol = config.convergenceTolerance;
    const medianPsi = medianOf(psi);
    const upper = 10 * sampleVariance(y) + Math.max(...psi);
    let s = start > 0 ? Math.min(start, upper) : Math.min(0.1 * medianOf(psi), upper);
    if (!(s > 0))
        s = Math.min(1e-6, upper);
    let iterations = 0;
    let converged = false;
    for (let it = 1; it <= config.maxIterations; it++) {
        iterations = it;
        const w = weightedQuantities(y, X, psi, s);
        const aScore = w.score + 1 / s;
        const aInfo = w.info + 1 / (s * s);
        if (!Number.isFinite(aScore) || !Number.isFinite(aInfo) || aInfo <= 0)
            break;
        let step = aScore / aInfo;
        let sNew = s + step;
        let halvings = 0;
        while ((!(sNew > 0) || sNew > upper || !Number.isFinite(sNew)) && halvings < 100) {
            step /= 2;
            sNew = s + step;
            halvings++;
        }
        if (!(sNew > 0) || sNew > upper || !Number.isFinite(sNew))
            break;
        if (Math.abs(sNew - s) < stepTolerance(tol, sNew, medianPsi)) {
            s = sNew;
            converged = true;
            break;
        }
        s = sNew;
    }
    return { sigma2: s, iterations, converged };
}
/** Estimate sigma2_v following the brief 9.7 fallback order. */
function fitVariance(y, X, psi, config) {
    const tol = config.convergenceTolerance;
    const medianPsi = medianOf(psi);
    const sigma2Pr = prasadRaoSigma2(y, X, psi);
    const start = Math.max(sigma2Pr, 0.1 * medianPsi);
    // Study-only variant (Tier 2 Item 5 arm A): the Li-Lahiri
    // adjusted REML for every cell, entered at the same start the
    // boundary branch below uses — so on a boundary dataset this
    // fit is bit-identical to the shipped one, and the ONLY
    // difference from the shipped policy is that interior datasets
    // take the adjusted fit too. The boundary flag stays false:
    // the fit did not reach the boundary; the adjusted estimator
    // was applied by policy (the shipped cascade already pairs
    // methodUsed 'adjusted-reml' with boundary false on its
    // non-convergence branch). Absent (every app run), the
    // fallback order below is the shipped one, unchanged.
    if (config.studyAdjustedRemlForAll === true) {
        const adj = fitAdjustedReml(y, X, psi, config, start);
        return {
            sigma2: adj.sigma2,
            methodUsed: 'adjusted-reml',
            iterations: adj.iterations,
            converged: adj.converged,
            boundary: false,
        };
    }
    let s = start;
    let iterations = 0;
    let converged = false;
    let boundary = start <= 0;
    if (!boundary) {
        for (let it = 1; it <= config.maxIterations; it++) {
            iterations = it;
            const w = weightedQuantities(y, X, psi, s);
            if (!Number.isFinite(w.score) || !Number.isFinite(w.info) || w.info <= 0) {
                break;
            }
            const sNew = s + w.score / w.info;
            if (sNew <= 0) {
                boundary = true;
                break;
            }
            if (Math.abs(sNew - s) < stepTolerance(tol, sNew, medianPsi)) {
                s = sNew;
                converged = true;
                break;
            }
            s = sNew;
        }
    }
    if (boundary) {
        const adj = fitAdjustedReml(y, X, psi, config, start);
        return {
            sigma2: adj.sigma2,
            methodUsed: 'adjusted-reml',
            iterations: adj.iterations,
            converged: adj.converged,
            boundary: true,
        };
    }
    if (!converged) {
        if (sigma2Pr > 0) {
            return {
                sigma2: sigma2Pr,
                methodUsed: 'prasad-rao',
                iterations,
                converged: false,
                boundary: false,
            };
        }
        const adj = fitAdjustedReml(y, X, psi, config, start);
        return {
            sigma2: adj.sigma2,
            methodUsed: 'adjusted-reml',
            iterations: adj.iterations,
            converged: adj.converged,
            boundary: false,
        };
    }
    return { sigma2: s, methodUsed: 'reml', iterations, converged: true, boundary: false };
}
/** Sample skewness and excess kurtosis of a vector (moment definitions). */
function skewnessKurtosis(values) {
    const n = values.length;
    if (n === 0)
        return { skewness: 0, kurtosis: 0 };
    const mean = meanOf(values);
    let m2 = 0;
    let m3 = 0;
    let m4 = 0;
    for (const v of values) {
        const d = v - mean;
        const d2 = d * d;
        m2 += d2;
        m3 += d2 * d;
        m4 += d2 * d2;
    }
    m2 /= n;
    m3 /= n;
    m4 /= n;
    if (m2 <= 0)
        return { skewness: 0, kurtosis: 0 };
    return {
        skewness: m3 / Math.pow(m2, 1.5),
        kurtosis: m4 / (m2 * m2) - 3,
    };
}
/**
 * Fit the Fay-Herriot model for one cell (brief 9.6-9.9). See the module
 * header for the formulas, the fallback order, and the area-count policy.
 *
 * Study-only dispatch (frontier workstream F-2): when the fit
 * configuration carries studyModelClass 'fh-t', the fit is delegated
 * to the Student-t random-effects class (fayherriot-t.ts), with this
 * normal fit as its registered decline fallback. Absent the switch —
 * every app run — the normal fit below runs exactly as shipped.
 */
export function fitFayHerriotImpl(domains, config) {
    if (config.studyModelClass === 'fh-t') {
        return fitFayHerriotTImpl(domains, config, fitFayHerriotNormalImpl);
    }
    return fitFayHerriotNormalImpl(domains, config);
}
function fitFayHerriotNormalImpl(domains, config) {
    const inSample = domains.filter((d) => !d.outOfSample);
    const m = inSample.length;
    if (m < MIN_AREAS_TO_FIT)
        throw new InsufficientAreasError(m, MIN_AREAS_TO_FIT);
    const p = inSample[0].covariates.length;
    if (p < 1) {
        throw new Error('fitFayHerriot: each domain must carry at least an intercept covariate.');
    }
    if (m - p < 1) {
        throw new Error(`fitFayHerriot: residual degrees of freedom (areas - covariates = ${m - p}) ` +
            'must be at least 1 for the Prasad-Rao starting value (brief 9.7).');
    }
    for (const d of domains) {
        if (d.covariates.length !== p) {
            throw new Error(`fitFayHerriot: inconsistent covariate length for area "${d.areaId}" ` +
                `(expected ${p}, received ${d.covariates.length}).`);
        }
        for (const v of d.covariates) {
            if (!Number.isFinite(v)) {
                throw new Error(`fitFayHerriot: non-finite covariate for area "${d.areaId}".`);
            }
        }
        if (!d.outOfSample) {
            if (!Number.isFinite(d.thetaHat)) {
                throw new Error(`fitFayHerriot: non-finite thetaHat for area "${d.areaId}".`);
            }
            if (!Number.isFinite(d.psi) || d.psi <= 0) {
                throw new Error(`fitFayHerriot: psi must be positive and finite for area "${d.areaId}"; received ${d.psi}.`);
            }
        }
    }
    // Standardization (brief 9.6): non-intercept columns to mean 0, SD 1
    // across the cell's in-sample areas. A zero-variance column passes
    // through unscaled; it is then collinear with the intercept and the
    // Cholesky step reports the design as singular.
    const centers = new Array(p).fill(0);
    const scales = new Array(p).fill(1);
    if (config.standardizeCovariates) {
        for (let j = 1; j < p; j++) {
            const column = inSample.map((d) => d.covariates[j]);
            const sd = Math.sqrt(sampleVariance(column));
            if (sd > 0 && Number.isFinite(sd)) {
                centers[j] = meanOf(column);
                scales[j] = sd;
            }
        }
    }
    const standardizeRow = (row) => row.map((v, j) => (v - centers[j]) / scales[j]);
    const X = inSample.map((d) => standardizeRow(d.covariates));
    const y = inSample.map((d) => d.thetaHat);
    const psi = inSample.map((d) => d.psi);
    const varianceFit = fitVariance(y, X, psi, config);
    const s = varianceFit.sigma2;
    const w = weightedQuantities(y, X, psi, s);
    const beta = w.beta;
    const betaStandardErrors = beta.map((_, j) => Math.sqrt(Math.max(0, w.invM[j][j])));
    // ML log-likelihood at the fitted values (brief 9.7).
    let sumLog = 0;
    let sumQr2 = 0;
    for (let i = 0; i < m; i++) {
        sumLog += Math.log(s + psi[i]);
        const q = 1 / (s + psi[i]);
        sumQr2 += q * w.residuals[i] * w.residuals[i];
    }
    const logLikMl = -0.5 * (m * Math.log(2 * Math.PI) + sumLog + sumQr2);
    const aic = -2 * logLikMl + 2 * (p + 1);
    const bic = -2 * logLikMl + (p + 1) * Math.log(m);
    // Asymptotic variance factor for g3 (brief 9.9).
    let vbar;
    if (varianceFit.methodUsed === 'prasad-rao') {
        let sumSq = 0;
        for (let i = 0; i < m; i++)
            sumSq += (s + psi[i]) * (s + psi[i]);
        vbar = (2 / (m * m)) * sumSq;
    }
    else {
        let sumNeg2 = 0;
        for (let i = 0; i < m; i++)
            sumNeg2 += Math.pow(s + psi[i], -2);
        vbar = 2 / sumNeg2;
    }
    const areas = domains.map((d) => {
        const xs = standardizeRow(d.covariates);
        const syntheticTheta = dot(xs, beta);
        const xInvMx = dot(xs, matVec(w.invM, xs));
        if (d.outOfSample) {
            return {
                areaId: d.areaId,
                outOfSample: true,
                gamma: 0,
                eblupTheta: syntheticTheta,
                syntheticTheta,
                g1: 0,
                g2: 0,
                g3: 0,
                mseTheta: s + xInvMx,
            };
        }
        const gamma = s / (s + d.psi);
        const g1 = gamma * d.psi;
        const g2 = (1 - gamma) * (1 - gamma) * xInvMx;
        const g3 = (d.psi * d.psi * Math.pow(s + d.psi, -3)) * vbar;
        return {
            areaId: d.areaId,
            outOfSample: false,
            thetaHat: d.thetaHat,
            psi: d.psi,
            gamma,
            eblupTheta: gamma * d.thetaHat + (1 - gamma) * syntheticTheta,
            syntheticTheta,
            g1,
            g2,
            g3,
            mseTheta: g1 + g2 + 2 * g3,
        };
    });
    // Diagnostics (brief 9.16), limited to what the fit inputs support.
    const inSampleFits = areas.filter((a) => !a.outOfSample);
    const standardizedResiduals = inSample.map((d, i) => {
        const value = w.residuals[i] / Math.sqrt(s + d.psi);
        return { areaId: d.areaId, value, flagged: Math.abs(value) > 3 };
    });
    const { skewness, kurtosis } = skewnessKurtosis(w.residuals);
    const medianGamma = medianOf(inSampleFits.map((a) => a.gamma));
    // Population counts are not part of FayHerriotDomainInput, so the
    // aggregation check falls back to unweighted means here; the analysis
    // layer, which holds the counts, computes the population-weighted
    // version the brief specifies.
    const modelMean = meanOf(inSampleFits.map((a) => a.eblupTheta));
    const directStatewide = meanOf(y);
    const relativeDifference = directStatewide !== 0
        ? (modelMean - directStatewide) / Math.abs(directStatewide)
        : modelMean === 0
            ? 0
            : Number.POSITIVE_INFINITY;
    const cvRatios = [];
    for (const a of inSampleFits) {
        if (a.thetaHat !== undefined && a.thetaHat !== 0 && a.eblupTheta !== 0) {
            const modelCv = Math.sqrt(Math.max(0, a.mseTheta)) / Math.abs(a.eblupTheta);
            const directCv = Math.sqrt(a.psi ?? 0) / Math.abs(a.thetaHat);
            if (directCv > 0)
                cvRatios.push(modelCv / directCv);
        }
    }
    const diagnostics = {
        standardizedResiduals,
        skewness,
        kurtosis,
        medianGamma,
        lowShrinkage: medianGamma < 0.1,
        aggregation: {
            modelMean,
            directStatewide,
            relativeDifference,
            warning: Math.abs(relativeDifference) > 0.05,
        },
        medianCvRatio: cvRatios.length > 0 ? medianOf(cvRatios) : 0,
    };
    // R2_FH (brief 9.16): variance of the intercept-only refit over the
    // same areas, by the same estimation path.
    let rSquaredFh;
    {
        const X0 = inSample.map(() => [1]);
        const nullFit = fitVariance(y, X0, psi, config);
        if (nullFit.sigma2 > 0) {
            rSquaredFh = Math.min(1, Math.max(0, 1 - s / nullFit.sigma2));
        }
    }
    if (rSquaredFh !== undefined)
        diagnostics.rSquaredFh = rSquaredFh;
    // Coefficients back on the original covariate scale (brief 9.6).
    const betaOriginalScale = beta.map((bj, j) => j === 0
        ? beta[0] -
            beta.reduce((acc, bk, k) => (k === 0 ? acc : acc + (bk * centers[k]) / scales[k]), 0)
        : bj / scales[j]);
    return {
        sigma2v: s,
        beta,
        betaStandardErrors,
        betaOriginalScale,
        methodUsed: varianceFit.methodUsed,
        iterations: varianceFit.iterations,
        converged: varianceFit.converged,
        boundary: varianceFit.boundary,
        logLikMl,
        aic,
        bic,
        areas,
        diagnostics,
    };
}
//# sourceMappingURL=fayherriot.js.map